/**
 * Helper Services Module
 * Utility functions for common operations
 */

class HelperServices {
  /**
   * Format date to locale string
   * @param {Date|string} date - Date to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted date string
   */
  formatDate(date, options = {}) {
    if (!date) return 'N/A';
    
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options,
    };

    return dateObj.toLocaleDateString('en-US', defaultOptions);
  }

  /**
   * Format date and time
   * @param {Date|string} date - Date to format
   * @returns {string} Formatted date and time string
   */
  formatDateTime(date) {
    if (!date) return 'N/A';
    
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    return dateObj.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Calculate days between two dates
   * @param {Date|string} date1 - First date
   * @param {Date|string} date2 - Second date (defaults to now)
   * @returns {number} Number of days between dates
   */
  daysBetween(date1, date2 = new Date()) {
    const d1 = date1 instanceof Date ? date1 : new Date(date1);
    const d2 = date2 instanceof Date ? date2 : new Date(date2);
    
    const diffTime = Math.abs(d2 - d1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Format number with specified decimal places
   * @param {number} value - Number to format
   * @param {number} decimals - Number of decimal places
   * @returns {string} Formatted number
   */
  formatNumber(value, decimals = 2) {
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }
    
    return Number(value).toFixed(decimals);
  }

  /**
   * Format number as percentage
   * @param {number} value - Number to format (0-1 or 0-100)
   * @param {boolean} isDecimal - Whether input is decimal (0-1) or percentage (0-100)
   * @returns {string} Formatted percentage
   */
  formatPercentage(value, isDecimal = true) {
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }
    
    const percentage = isDecimal ? value * 100 : value;
    return `${percentage.toFixed(1)}%`;
  }

  /**
   * Format coordinates for display
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {number} precision - Decimal precision
   * @returns {string} Formatted coordinates
   */
  formatCoordinates(lat, lng, precision = 4) {
    if (lat === null || lng === null || lat === undefined || lng === undefined) {
      return 'N/A';
    }
    
    return `${lat.toFixed(precision)}, ${lng.toFixed(precision)}`;
  }

  /**
   * Debounce function execution
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  debounce(func, wait = 300) {
    let timeout;
    
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Throttle function execution
   * @param {Function} func - Function to throttle
   * @param {number} limit - Time limit in milliseconds
   * @returns {Function} Throttled function
   */
  throttle(func, limit = 300) {
    let inThrottle;
    
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /**
   * Deep clone an object
   * @param {*} obj - Object to clone
   * @returns {*} Cloned object
   */
  deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    
    if (obj instanceof Array) {
      return obj.map(item => this.deepClone(item));
    }
    
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = this.deepClone(obj[key]);
      }
    }
    
    return clonedObj;
  }

  /**
   * Check if object is empty
   * @param {Object} obj - Object to check
   * @returns {boolean} True if empty
   */
  isEmpty(obj) {
    if (!obj) return true;
    
    if (Array.isArray(obj)) {
      return obj.length === 0;
    }
    
    if (typeof obj === 'object') {
      return Object.keys(obj).length === 0;
    }
    
    return false;
  }

  /**
   * Generate unique ID
   * @param {string} prefix - Optional prefix
   * @returns {string} Unique ID
   */
  generateId(prefix = '') {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 9);
    return prefix ? `${prefix}_${timestamp}_${random}` : `${timestamp}_${random}`;
  }

  /**
   * Validate email address
   * @param {string} email - Email to validate
   * @returns {boolean} True if valid
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Get color for status
   * @param {string} status - Status value
   * @returns {string} Color code
   */
  getStatusColor(status) {
    const colors = {
      active: '#4caf50',
      success: '#4caf50',
      excellent: '#4caf50',
      maintenance: '#ff9800',
      warning: '#ff9800',
      good: '#2196f3',
      inactive: '#f44336',
      error: '#f44336',
      poor: '#f44336',
      fair: '#ffc107',
    };
    
    return colors[status?.toLowerCase()] || '#9e9e9e';
  }

  /**
   * Sort array of objects by property
   * @param {Array} array - Array to sort
   * @param {string} property - Property to sort by
   * @param {string} order - Sort order ('asc' or 'desc')
   * @returns {Array} Sorted array
   */
  sortByProperty(array, property, order = 'asc') {
    if (!Array.isArray(array) || array.length === 0) {
      return array;
    }
    
    const sorted = [...array].sort((a, b) => {
      const aVal = this.getNestedProperty(a, property);
      const bVal = this.getNestedProperty(b, property);
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    
    return order === 'desc' ? sorted.reverse() : sorted;
  }

  /**
   * Get nested property from object
   * @param {Object} obj - Object to access
   * @param {string} path - Property path (e.g., 'user.address.city')
   * @returns {*} Property value
   */
  getNestedProperty(obj, path) {
    if (!obj || !path) return null;
    
    return path.split('.').reduce((current, prop) => {
      return current?.[prop];
    }, obj);
  }

  /**
   * Create download link for file
   * @param {string} content - File content (base64 or plain text)
   * @param {string} filename - Filename for download
   * @param {string} contentType - MIME type
   * @param {boolean} isBase64 - Whether content is base64 encoded
   */
  downloadFile(content, filename, contentType = 'text/plain', isBase64 = false) {
    let dataUrl;
    
    if (isBase64) {
      dataUrl = `data:${contentType};base64,${content}`;
    } else {
      const blob = new Blob([content], { type: contentType });
      dataUrl = URL.createObjectURL(blob);
    }
    
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    if (!isBase64) {
      URL.revokeObjectURL(dataUrl);
    }
  }

  /**
   * Capitalize first letter of string
   * @param {string} str - String to capitalize
   * @returns {string} Capitalized string
   */
  capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Convert camelCase to readable text
   * @param {string} str - CamelCase string
   * @returns {string} Readable text
   */
  camelToReadable(str) {
    if (!str) return '';
    return str.replace(/([A-Z])/g, ' $1').trim();
  }

  /**
   * Truncate text with ellipsis
   * @param {string} text - Text to truncate
   * @param {number} maxLength - Maximum length
   * @returns {string} Truncated text
   */
  truncate(text, maxLength = 50) {
    if (!text || text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  }

  /**
   * Parse query string parameters
   * @param {string} queryString - Query string to parse
   * @returns {Object} Parsed parameters
   */
  parseQueryString(queryString) {
    const params = {};
    const searchParams = new URLSearchParams(queryString);
    
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    
    return params;
  }

  /**
   * Build query string from object
   * @param {Object} params - Parameters object
   * @returns {string} Query string
   */
  buildQueryString(params) {
    if (!params || this.isEmpty(params)) return '';
    
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        searchParams.append(key, value);
      }
    });
    
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Export singleton instance
const helperServices = new HelperServices();
export default helperServices;

// Export bound individual methods for convenience
export const formatDate = helperServices.formatDate.bind(helperServices);
export const formatDateTime = helperServices.formatDateTime.bind(helperServices);
export const daysBetween = helperServices.daysBetween.bind(helperServices);
export const formatNumber = helperServices.formatNumber.bind(helperServices);
export const formatPercentage = helperServices.formatPercentage.bind(helperServices);
export const formatCoordinates = helperServices.formatCoordinates.bind(helperServices);
export const debounce = helperServices.debounce.bind(helperServices);
export const throttle = helperServices.throttle.bind(helperServices);
export const deepClone = helperServices.deepClone.bind(helperServices);
export const isEmpty = helperServices.isEmpty.bind(helperServices);
export const generateId = helperServices.generateId.bind(helperServices);
export const isValidEmail = helperServices.isValidEmail.bind(helperServices);
export const getStatusColor = helperServices.getStatusColor.bind(helperServices);
export const sortByProperty = helperServices.sortByProperty.bind(helperServices);
export const getNestedProperty = helperServices.getNestedProperty.bind(helperServices);
export const downloadFile = helperServices.downloadFile.bind(helperServices);
export const capitalize = helperServices.capitalize.bind(helperServices);
export const camelToReadable = helperServices.camelToReadable.bind(helperServices);
export const truncate = helperServices.truncate.bind(helperServices);
export const parseQueryString = helperServices.parseQueryString.bind(helperServices);
export const buildQueryString = helperServices.buildQueryString.bind(helperServices);
// Ref/flow status filter matching, shared by the map (App.jsx) and the
// station dropdown (StationSelectorModal.jsx).
// UNKNOWN_STATUS is the filter value for the "Unknown" option. It matches
// any status the map draws grey (see getStatusColor/getFlowStatusColor in
// CaliforniaMap.jsx): null, empty, "Unknown"-like text, or any value that is
// not one of the named categories below.
export const UNKNOWN_STATUS = '__unknown__';
const KNOWN_REF_STATUSES = ['reference', 'intermediate', 'stressed'];
const KNOWN_FLOW_STATUSES = ['P', 'NP', 'I', 'RFI', 'SFI', 'E'];

const splitStatusValues = (raw, normalize) => {
  const values = (raw ?? '').toString().split(',').map(v => normalize(v.trim()));
  return values.length ? values : [''];
};

const matchesStatus = (raw, filter, known, normalize) => {
  if (!filter) return true;
  const values = splitStatusValues(raw, normalize);
  if (filter === UNKNOWN_STATUS) return values.some(v => !known.includes(v));
  return values.includes(normalize(filter));
};

export const matchesRefStatusFilter = (raw, filter) =>
  matchesStatus(raw, filter, KNOWN_REF_STATUSES, v => v.toLowerCase());

export const matchesFlowStatusFilter = (raw, filter) =>
  matchesStatus(raw, filter, KNOWN_FLOW_STATUSES, v => v.toUpperCase());
